import {
  format,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInMonths,
  differenceInYears,
  parseISO,
  addDays,
  startOfDay,
  subDays,
  startOfWeek,
  startOfMonth,
  subMonths,
  formatISO,
} from "date-fns";
import { zeroDate } from "src/constants";
import type { FetchFromApiType } from "src/hooks/api/useApiClient";

export const EMAIL_REGEX =
  /^[a-zA-Z0-9._%+-]+@(?!(?:[-.])|.*\.\.)[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;
export const PHONE_REGEX = /^(\+1\d{10}|\d{10})$/;
export const SSN_REGEX = /^\d{9}$/;

export const validEmail = (val: string): boolean =>
  Boolean(val && EMAIL_REGEX.test(val));

export const validName = (val: string): boolean =>
  Boolean(val && /^[A-Za-zÀ-ÖØ-öø-ÿ '-]+$/.test(val));

export const validPhoneNumber = (value: string): boolean =>
  PHONE_REGEX.test(value);

export const addCountryCode = (phoneNumber: string): string =>
  phoneNumber.startsWith("+1") ? phoneNumber : `+1${phoneNumber}`;

export const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return "";

  const cleaned = phoneNumber.replace(/\D/g, "");

  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  } else if (cleaned.length === 7) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  }

  return phoneNumber;
};

export const shortMonthFromDate = (dateString: string) => {
  if (!dateString) return "";
  return format(addDays(parseISO(dateString), 1), "MMM");
};

export const dayOfMonthFromDate = (dateString: string) => {
  if (!dateString) return "";
  return format(addDays(parseISO(dateString), 1), "d");
};

export const dayAndDateFromDate = (dateString: string) => {
  if (!dateString || dateString === zeroDate) return "";
  return format(addDays(parseISO(dateString), 1), "EEEE, MMMM dd");
};
export const timeAgo = (dateString: string) => {
  if (dateString === zeroDate) return "----";
  const date = new Date(dateString);
  const now = new Date();

  const diffInYearsVal = differenceInYears(now, date);
  if (diffInYearsVal >= 1) {
    return format(date, "MM-dd-yyyy");
  }

  const diffInMonthsVal = differenceInMonths(now, date);
  if (diffInMonthsVal >= 1) {
    return `${diffInMonthsVal} month${diffInMonthsVal > 1 ? "s" : ""} ago`;
  }

  const diffInDaysVal = differenceInDays(now, date);
  if (diffInDaysVal >= 1) {
    return `${diffInDaysVal} day${diffInDaysVal > 1 ? "s" : ""} ago`;
  }

  const diffInHoursVal = differenceInHours(now, date);
  if (diffInHoursVal >= 1) {
    return `${diffInHoursVal} hour${diffInHoursVal > 1 ? "s" : ""} ago`;
  }

  const diffInMinutesVal = differenceInMinutes(now, date);
  if (diffInMinutesVal >= 1) {
    return `${diffInMinutesVal} minute${diffInMinutesVal > 1 ? "s" : ""} ago`;
  }

  return "Just now";
};

export const dateRangefromDatePeriod = (period: string) => {
  const now = new Date();
  const end = formatISO(now, { representation: "date" }) + "T23:59:59.000Z";
  let start;

  switch (period) {
    case "Today":
      start =
        formatISO(startOfDay(now), { representation: "date" }) +
        "T00:00:00.000Z";
      break;
    case "Yesterday":
      start =
        formatISO(startOfDay(subDays(now, 1)), { representation: "date" }) +
        "T00:00:00.000Z";
      break;
    case "This week":
      start =
        formatISO(startOfWeek(now, { weekStartsOn: 0 }), {
          representation: "date",
        }) + "T00:00:00.000Z"; // Assuming week starts on Sunday
      break;
    case "This month":
      start =
        formatISO(startOfMonth(now), { representation: "date" }) +
        "T00:00:00.000Z";
      break;
    case "Last month":
      start =
        formatISO(startOfMonth(subMonths(now, 1)), { representation: "date" }) +
        "T00:00:00.000Z";
      break;
    default:
      return {
        end: null,
        start: null,
        error: `Invalid period: "${period}". Please use "Today", "Yesterday", "This week", "This month", or "Last month".`,
      };
  }

  return {
    end,
    start,
    period,
  };
};

export const formatDateRangeShort = (startDate: string, endDate: string) => {
  if (!startDate || !endDate) {
    return "";
  }
  const parsedStartDate = addDays(parseISO(startDate), 1); // addDays is a dumb hack for UTC conversion
  const parsedEndDate = addDays(parseISO(endDate), 1); // addDays is a dumb hack for UTC conversion

  if (isNaN(parsedEndDate.getTime()) || isNaN(parsedEndDate.getTime())) {
    return ""; // Handle invalid date strings
  }

  const startMonth = format(parsedStartDate, "MMM");
  const startDay = format(parsedStartDate, "d");
  const endDay = format(parsedEndDate, "d");

  return `${startMonth} ${startDay}-${endDay}`;
};

// Can be used to create a date string in the format 'YYYY-MM-DD'
// Can also be used for the above function issues with UTC conversion.
export const createIsoDate = (
  year: number,
  month: number,
  day: number = 1,
): string => {
  const date = new Date(Date.UTC(year, month - 1, day)); // Create UTC date
  return date.toISOString(); // Stores as 'YYYY-MM-DDT00:00:00.000Z'
};

export const formatNumberForCurrency = (num: number): string => {
  return Number(num).toLocaleString("en", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const extractFilenameFromContentDisposition = (
  headers: Headers,
): string | null => {
  const contentDisposition = headers.get("content-disposition");
  if (!contentDisposition) return null;

  const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/i);
  if (filenameStarMatch) {
    return decodeURIComponent(filenameStarMatch[1]);
  }

  const filenameMatch = contentDisposition.match(
    /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i,
  );
  if (filenameMatch) {
    let filename = filenameMatch[1];

    if (
      filename.charAt(0) === '"' &&
      filename.charAt(filename.length - 1) === '"'
    ) {
      filename = filename.slice(1, -1);
    }
    return filename;
  }

  return null;
};

export const downloadBlobFile = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.target = "_blank";
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 5000);
};

export const downloadDocument = async (
  fetchFromApi: FetchFromApiType,
  safeApiCall: (
    apiCall: () => Promise<{ blob: Blob; headers: Headers }>,
    options?: { suppressErrorRedirect?: boolean },
  ) => Promise<{ blob: Blob; headers: Headers }>,
  path: string,
  fallbackFilename?: string,
  viewOnly = false,
) => {
  try {
    const response = await safeApiCall(
      () =>
        fetchFromApi(path, { responseType: "blob" }) as Promise<{
          blob: Blob;
          headers: Headers;
        }>,
    );
    const { blob, headers } = response;

    let filename = extractFilenameFromContentDisposition(headers);

    if (!filename && fallbackFilename) {
      filename = fallbackFilename;
    }

    const url = window.URL.createObjectURL(blob);

    if (viewOnly) {
      window.open(url, "_blank");
    } else {
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || "document.pdf";
      link.target = "_blank";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 5000);
  } catch (error) {
    console.error("Failed to process document:", error);
    throw error;
  }
};
